import { useState } from "react"
import { router, Link } from "@inertiajs/react"
import { toast } from "sonner"
import { ArrowLeft, Pencil } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { LineChart, type LineChartSeries } from "@/components/ui/line-chart"

// Settings → Apps → Show. Read-only summary + admin-gated status
// transitions and a 50-row health log. Splits nicely into four panels:
// Manifest / Endpoints / Status actions / Health.

type AppStatus = "draft" | "private" | "public" | "archived"
type Placement = { context: string; location: string }

type Endpoint = {
  id: number
  role: "document" | "error_webhook"
  httpMethod: string
  url: string
  querySchema: unknown
  timeoutMs: number
  cacheTtlSeconds: number
  supportedContextVars: string[]
  description: string | null
}

type AppDetail = {
  id: number
  key: string
  name: string
  description: string | null
  icon: string | null
  status: AppStatus
  manifestVersion: number
  defaultMinRole: "agent" | "agent_manager" | "admin"
  supportedPlacements: Placement[]
  installCount: number
  hasInstalls: boolean
  publisherCompany: { id: number; name: string }
  endpoints: Endpoint[]
}

type HealthEvent = {
  id: number
  kind: string
  recordedAt: string | null
  companyAppId: number | null
  installCompany:  string | null
  installContext:  string | null
  installLocation: string | null
  schemaPath: string | null
  blockIndex: number | null
  blockType: string | null
  statusCode: number | null
  message: string | null
}

type HealthChart = {
  dates:  string[]
  series: { kind: string; total: number; daily: number[] }[]
}

type Install = {
  id: number
  companyId: number
  companyName: string | null
  enabled: boolean | null
  createdAt: string | null
}

type Props = {
  app: AppDetail
  healthEvents: HealthEvent[]
  healthChart:  HealthChart
  installs: Install[]
  isAdmin: boolean
}

const STATUS_TONE: Record<AppStatus, BadgeTone> = {
  draft:    "warning",
  private:  "neutral",
  public:   "success",
  archived: "neutral",
}

const STATUS_LABEL: Record<AppStatus, string> = {
  draft:    "Draft",
  private:  "Private",
  public:   "Public",
  archived: "Archived",
}

const HEALTH_TONE: Record<string, BadgeTone> = {
  fetch_5xx:             "error",
  fetch_4xx:             "error",
  fetch_timeout:         "error",
  schema_invalid:        "warning",
  url_not_allowlisted:   "error",
  private_ip_rejected:   "error",
  oversized:             "warning",
  bad_method:            "warning",
  parse_error:           "warning",
  refresh_unknown_id:    "warning",
  refresh_block_invalid: "warning",
}

// Stable color per kind so the legend / line / table badge agree at a
// glance. Red family for fetch failures (server errors / blocked
// requests), amber for protocol mismatches (schema / oversized / etc.).
const HEALTH_KIND_COLOR: Record<string, string> = {
  fetch_5xx:             "#ef4444", // red-500
  fetch_4xx:             "#f97316", // orange-500
  fetch_timeout:         "#dc2626", // red-600
  schema_invalid:        "#eab308", // yellow-500 — developer-side schema bug, warning
  url_not_allowlisted:   "#b91c1c", // red-700
  private_ip_rejected:   "#991b1b", // red-800
  oversized:             "#facc15", // yellow-400
  bad_method:            "#ca8a04", // yellow-600
  parse_error:           "#a16207", // yellow-700 — yellow family for protocol/format issues
  refresh_unknown_id:    "#f59e0b", // amber-500
  refresh_block_invalid: "#d97706", // amber-600
}

export default function SettingsAppsShow({ app, healthEvents, healthChart, installs, isAdmin }: Props) {
  return (
    <AppShell>
      <PageHeader
        eyebrow={
          <Link href="/settings/apps" className="inline-flex items-center gap-1 hover:text-app-text">
            <ArrowLeft size={12} strokeWidth={2} /> All Apps
          </Link>
        }
        title={app.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <code className="break-all rounded bg-white/5 px-1.5 py-0.5 font-mono text-xs text-app-text">{app.key}</code>
            <Badge tone={STATUS_TONE[app.status]}>{STATUS_LABEL[app.status]}</Badge>
            <span className="text-xs text-app-muted">v{app.manifestVersion}</span>
          </span>
        }
        actions={isAdmin ? (
          <Link href={`/settings/apps/${app.id}/edit`}>
            <Button variant="ghost">
              <Pencil size={14} strokeWidth={2.5} className="mr-1" /> Edit
            </Button>
          </Link>
        ) : undefined}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <ManifestPanel app={app} />
          <EndpointsPanel endpoints={app.endpoints} />
          <HealthPanel events={healthEvents} chart={healthChart} />
        </div>
        <div className="space-y-5">
          {isAdmin && <StatusActionsPanel app={app} />}
          <InstallsPanel installs={installs} />
        </div>
      </div>
    </AppShell>
  )
}

function ManifestPanel({ app }: { app: AppDetail }) {
  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-5">
        <h2 className="text-lg font-semibold">Manifest</h2>
        <p className="text-sm text-app-muted">Identity-only metadata.</p>
      </header>
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row label="Description" value={app.description || "—"} />
        <Row label="Icon" value={app.icon || "—"} mono />
        <Row label="Default min role" value={app.defaultMinRole} mono />
        <Row label="Publisher" value={app.publisherCompany.name} />
        <div className="sm:col-span-2">
          <div className="text-xs uppercase tracking-wide text-app-subtle">Supported placements</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {app.supportedPlacements.length === 0
              ? <span className="text-sm text-app-subtle">—</span>
              : app.supportedPlacements.map((p, idx) => (
                <span
                  key={`${p.context}.${p.location}.${idx}`}
                  className="inline-flex items-center rounded-chip border border-border bg-white/5 px-2 py-0.5 font-mono text-[11px] text-app-muted"
                >
                  {p.context}.{p.location}
                </span>
              ))}
          </div>
        </div>
      </dl>
    </Card>
  )
}

function EndpointsPanel({ endpoints }: { endpoints: Endpoint[] }) {
  return (
    <Card className="overflow-hidden p-0">
      <header className="border-b border-border px-5 py-4 sm:px-6">
        <h2 className="text-lg font-semibold">Endpoints</h2>
        <p className="text-sm text-app-muted">Wired into Companies::Client when an install resolves a placement.</p>
      </header>
      {endpoints.length === 0 ? (
        <div className="px-5 py-5 text-sm text-app-muted sm:px-6">No endpoints configured.</div>
      ) : (
        <>
          {/* Desktop: table */}
          <table className="hidden w-full text-sm md:table">
            <thead>
              <tr className="border-b border-border bg-white/[0.03] text-xs uppercase tracking-wide text-app-subtle">
                <th className="px-5 py-3 text-left font-medium">Role</th>
                <th className="px-4 py-3 text-left font-medium">Method</th>
                <th className="px-4 py-3 text-left font-medium">Path template</th>
                <th className="px-4 py-3 text-left font-medium">Context vars</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {endpoints.map((ep) => (
                <tr key={ep.id}>
                  <td className="px-5 py-3 font-mono text-xs text-app-text">{ep.role}</td>
                  <td className="px-4 py-3 font-mono text-xs text-app-muted">{ep.httpMethod}</td>
                  <td className="px-4 py-3 font-mono text-xs text-app-text break-all">{ep.url}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {ep.supportedContextVars.length === 0
                        ? <span className="text-xs text-app-subtle">—</span>
                        : ep.supportedContextVars.map((v) => (
                          <span key={v} className="inline-flex items-center rounded-chip border border-border bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-app-muted">
                            {v}
                          </span>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile/tablet: card list */}
          <ul className="divide-y divide-border md:hidden">
            {endpoints.map((ep) => (
              <li key={ep.id} className="space-y-2 px-5 py-4 text-sm">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-app-text">{ep.role}</span>
                  <span className="text-app-subtle">·</span>
                  <span className="font-mono text-app-muted">{ep.httpMethod}</span>
                </div>
                <div className="break-all font-mono text-xs text-app-text">{ep.url}</div>
                {ep.supportedContextVars.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {ep.supportedContextVars.map((v) => (
                      <span key={v} className="inline-flex items-center rounded-chip border border-border bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-app-muted">
                        {v}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  )
}

function StatusActionsPanel({ app }: { app: AppDetail }) {
  const [busy, setBusy] = useState<AppStatus | null>(null)

  function go(to: AppStatus) {
    if (to === "archived") {
      if (!confirm(`Archive '${app.name}'? Existing installs keep functioning, but the App is hidden from new installations.`)) return
    }
    setBusy(to)
    const url = to === "archived"
      ? `/settings/apps/${app.id}/archive`
      : `/settings/apps/${app.id}/transition_status`
    const data = to === "archived" ? {} : { to }
    router.post(url, data, {
      preserveScroll: true,
      onSuccess: () => toast.success(`Status: ${STATUS_LABEL[to]}`),
      onError:   (e) => toast.error(e.base ?? "Could not change status"),
      onFinish:  () => setBusy(null),
    })
  }

  const STATES: AppStatus[] = ["draft", "private", "public", "archived"]
  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-4">
        <h2 className="text-base font-semibold">Lifecycle</h2>
        <p className="text-xs text-app-muted">Phase 1: any transition is allowed.</p>
      </header>
      <div className="flex flex-col gap-2">
        {STATES.map((s) => {
          const active = s === app.status
          return (
            <Button
              key={s}
              type="button"
              variant={active ? "gold" : "ghost"}
              disabled={active || busy != null}
              onClick={() => go(s)}
              className="justify-start"
            >
              {active ? `Currently ${STATUS_LABEL[s]}` : `Mark ${STATUS_LABEL[s]}`}
            </Button>
          )
        })}
      </div>
    </Card>
  )
}

function InstallsPanel({ installs }: { installs: Install[] }) {
  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-4">
        <h2 className="text-base font-semibold">Installs <span className="ml-1 text-sm font-normal text-app-muted">({installs.length})</span></h2>
        <p className="text-xs text-app-muted">Tenants that currently have this App enabled.</p>
      </header>
      {installs.length === 0 ? (
        <div className="text-sm text-app-muted">No installs yet.</div>
      ) : (
        <ul className="space-y-2">
          {installs.map((i) => (
            <li key={i.id} className="flex items-center justify-between rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm">
              <div>
                <div className="text-app-text">{i.companyName ?? `company #${i.companyId}`}</div>
                <div className="text-[10px] text-app-subtle">{i.createdAt ? new Date(i.createdAt).toLocaleDateString() : "—"}</div>
              </div>
              {i.enabled === false && <Badge tone="warning">disabled</Badge>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function HealthPanel({ events, chart }: { events: HealthEvent[]; chart: HealthChart }) {
  return (
    <Card className="overflow-hidden p-0">
      <header className="border-b border-border px-5 py-4 sm:px-6">
        <h2 className="text-lg font-semibold">Health <span className="ml-1 text-sm font-normal text-app-muted">(last {events.length})</span></h2>
        <p className="text-sm text-app-muted">Validation / fetch failures recorded across all installs.</p>
      </header>

      <HealthChartPanel chart={chart} />

      {events.length === 0 ? (
        <div className="px-5 py-5 text-sm text-app-muted sm:px-6">No health events recorded yet.</div>
      ) : (
        <>
          {/* Desktop: table */}
          <table className="hidden w-full text-sm lg:table">
            <thead>
              <tr className="border-b border-border bg-white/[0.03] text-xs uppercase tracking-wide text-app-subtle">
                <th className="px-5 py-3 text-left font-medium">When</th>
                <th className="px-4 py-3 text-left font-medium">Kind</th>
                <th className="px-4 py-3 text-left font-medium">Install</th>
                <th className="px-4 py-3 text-left font-medium">Schema path</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="px-5 py-3 text-xs text-app-muted">
                    {e.recordedAt ? new Date(e.recordedAt).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={HEALTH_TONE[e.kind] ?? "neutral"}>{e.kind}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-app-muted">
                    <InstallCell event={e} />
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-app-text">{e.schemaPath ?? "—"}</td>
                  <td className="px-4 py-3 text-xs text-app-muted">
                    {e.statusCode ?? "—"}
                    {e.blockIndex != null && <span className="ml-1">(block {e.blockIndex} {e.blockType})</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-app-text">{e.message ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile/tablet: card list */}
          <ul className="divide-y divide-border lg:hidden">
            {events.map((e) => (
              <li key={e.id} className="space-y-2 px-5 py-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={HEALTH_TONE[e.kind] ?? "neutral"}>{e.kind}</Badge>
                  <span className="text-xs text-app-muted">
                    {e.recordedAt ? new Date(e.recordedAt).toLocaleString() : "—"}
                  </span>
                  {e.statusCode != null && (
                    <span className="font-mono text-[11px] text-app-muted">· {e.statusCode}</span>
                  )}
                </div>
                <div className="text-xs text-app-muted">
                  <InstallCell event={e} />
                </div>
                {e.schemaPath && (
                  <div className="break-all font-mono text-[11px] text-app-text">{e.schemaPath}</div>
                )}
                {e.blockIndex != null && (
                  <div className="text-xs text-app-subtle">block {e.blockIndex} {e.blockType}</div>
                )}
                {e.message && (
                  <div className="text-xs text-app-text">{e.message}</div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  )
}

// "Playful · ticket / sidebar_tab" beats "install #2".
function InstallCell({ event }: { event: HealthEvent }) {
  if (!event.companyAppId) return <span className="italic text-app-subtle">all installs</span>
  const placement = [event.installContext, event.installLocation].filter(Boolean).join(" / ")
  return (
    <span>
      <span className="font-medium text-app-text">{event.installCompany ?? `install #${event.companyAppId}`}</span>
      {placement && <span className="ml-1 text-app-subtle">· {placement}</span>}
    </span>
  )
}

// 30-day per-kind error chart. One line per kind that fired in the
// window. Y-axis = daily count, X-axis = date. Empty window renders an
// unobtrusive placeholder so the panel collapses cleanly. Reuses the
// generic <LineChart> primitive (recharts wrapper).
function HealthChartPanel({ chart }: { chart: HealthChart }) {
  if (!chart || chart.series.length === 0) {
    return (
      <div className="border-b border-border px-5 py-3 text-xs text-app-muted sm:px-6">
        No errors in the last 30 days.
      </div>
    )
  }
  const series: LineChartSeries[] = chart.series.map((s) => ({
    id:    s.kind,
    label: s.kind,
    data:  s.daily,
    color: HEALTH_KIND_COLOR[s.kind] ?? "#94a3b8", // slate-400 fallback
  }))
  return (
    <div className="border-b border-border px-5 py-4 sm:px-6">
      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-xs uppercase tracking-wide text-app-subtle">Last 30 days</span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          {chart.series.map((s) => (
            <span key={s.kind} className="inline-flex items-center gap-1">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: HEALTH_KIND_COLOR[s.kind] ?? "#94a3b8" }}
                aria-hidden
              />
              <span className="text-app-text">{s.kind}</span>
              <span className="text-app-subtle">· {s.total}</span>
            </span>
          ))}
        </div>
      </div>
      <LineChart dates={chart.dates} series={series} height={200} />
    </div>
  )
}

function Row({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-app-subtle">{label}</dt>
      <dd className={mono ? "mt-1 font-mono text-sm text-app-text" : "mt-1 text-sm text-app-text"}>{value}</dd>
    </div>
  )
}
